import { z } from 'zod';
/**
 * Контракты глобального поиска «Поиск везде» — `GET /api/search`
 * (см. `docs/api-design.md §«Глобальный поиск»`, `docs/modules/search.md`).
 *
 * Поиск кросс-сущностный: одним запросом ищет записи (items), пространства
 * и справочники. Скоуп прав для записей/пространств зеркалит
 * `modules/spaces/service.ts → listSpacesForUser` (только пространства,
 * где пользователь участник, либо `isPlatformAdmin` → все). Справочники
 * системного уровня — доступны любому аутентифицированному.
 *
 * В MVP матчинг — простой ILIKE по `display_key` и текстовым `value_text`
 * (поля text/textarea/markdown/url). Полнотекста/tsvector нет (см.
 * `docs/modules/search.md §«Что НЕ делаем в MVP»`).
 */
// ─── GET /api/search (query) ─────────────────────────────────────────────
//
// `q` тримится в хендлере; после trim длина < 2 → 200 с пустыми массивами
// (это не ошибка, UI зовёт свободно). `limit` — лимит НА КАЖДУЮ категорию,
// целое 1..20, по умолчанию 8; невалидное значение клампится в хендлере.
export const GlobalSearchQuerySchema = z.object({
    q: z.string(),
    limit: z.number().int().min(1).max(20).optional(),
});
// ─── Совпадение по записи (item) ─────────────────────────────────────────
//
// `title` резолвится через поле item_type с `config->>'isTitle' = 'true'`
// (фолбэк — поле со `slug='title'`), иначе null. `snippet` — обрезанный
// (~120 симв.) фрагмент совпавшего `value_text`; null, если совпало только
// по `displayKey`. Навигация на фронте:
// `/spaces/${spaceId}?item=${displayKey ?? itemId}`.
export const GlobalSearchItemHitSchema = z.object({
    itemId: z.string().uuid(),
    spaceId: z.string().uuid(),
    spaceName: z.string(),
    spaceSlug: z.string(),
    displayKey: z.string().nullable(),
    title: z.string().nullable(),
    itemTypeName: z.string(),
    snippet: z.string().nullable(),
});
// ─── Совпадение по пространству ──────────────────────────────────────────
//
// Навигация на фронте: `/spaces/${id}`.
export const GlobalSearchSpaceHitSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    slug: z.string(),
    icon: z.string().nullable(),
});
// ─── Совпадение по справочнику ───────────────────────────────────────────
//
// Навигация на фронте: `/dictionaries/${id}`.
export const GlobalSearchDictionaryHitSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
});
// ─── Ответ GET /api/search ───────────────────────────────────────────────
//
// `query` — эхо trimmed-запроса. Каждая категория обрезана до `limit`.
export const GlobalSearchResponseSchema = z.object({
    query: z.string(),
    items: z.array(GlobalSearchItemHitSchema),
    spaces: z.array(GlobalSearchSpaceHitSchema),
    dictionaries: z.array(GlobalSearchDictionaryHitSchema),
});
